<template>
  <article class="cmp cmp-product-thumb" :class="itemClass">
    <div :class="paddingClasses + ' ' + thumbContainerClass" :style="paddingInlineStyles">
      <template v-if="showBasket">
        <custom-add-to-basket
            data-testing="item-add-to-basket"
            :variation-id="selectedVariation.variation.id"
            :is-salable="!!selectedVariation.filter && selectedVariation.filter.isSalable"
            :has-children="!!selectedVariation.item && selectedVariation.item.salableVariationCount > 1"
            :interval-quantity="selectedVariation.variation.intervalOrderQuantity || 1"
            :minimum-quantity="selectedVariation.variation.minimumOrderQuantity"
            :maximum-quantity="!!selectedVariation.variation.maximumOrderQuantity && selectedVariation.variation.maximumOrderQuantity > 0 ? selectedVariation.variation.maximumOrderQuantity : null"
            :order-properties="selectedVariation.properties.filter(function(prop) { return prop.property.isOderProperty })"
            :has-order-properties="selectedVariation.hasOrderProperties"
            :has-required-order-property="selectedVariation.hasRequiredOrderProperty"
            :use-large-scale="true"
            :show-quantity="false"
            :item-url="selectedVariation | itemURL(urlWithVariationId)"
            :has-price="selectedVariation | hasItemDefaultPrice"
            :has-graduated-price="itemGraduatedPriceisCheapestSorting || itemGraduatedPricesalableVariationCount"
            :item-type="selectedVariation.item.itemType"
            :basket-not-salable-icon="basketNotSalableIcon"
            :basket-salable-icon="basketSalableIcon">
        </custom-add-to-basket>
      </template>

      <div class="mb-2" :class="thumbImageClass">
        <!-- STORE SPECIALS -->
        <slot name="store-special">
          <custom-item-store-special
              v-if="storeSpecial || item.variation.bundleType === 'bundle' || item.item.itemType === 'set' || !item.filter.isSalable || selectedVariation.tags"
              :store-special="storeSpecial"
              :recommended-retail-price="item.prices.rrp"
              :variation-retail-price="item.prices.default"
              :special-offer-price="item.prices.specialOffer"
              :decimal-count="decimalCount"
              :bundle-type="item.variation.bundleType"
              :item-type="item.item.itemType"
              :is-salable="!!item.item && (item.item.salableVariationCount > 0)"
              :badge-available-class="badgeClass"
              :badge-available-text="badgeText"
              :display-av-badge="enableBadge"
              :enable-tag-badge="enableTagBadge"
              :badge-position="badgePosition"
              :selected-variation="selectedVariation"
          />
        </slot>
        <!-- CAROUSEL -->
        <div :class="'prop-' + aspectRatio">
          <slot name="item-image">
            <custom-category-image
                ref="CustomCategoryImage"
                :image-urls-data="selectedVariation.images | itemImages(imageUrlAccessor)"
                :alt="item | itemName"
                :title="item | itemName"
                :item-url="selectedVariation | itemURL(urlWithVariationId)"
                :enable-carousel="$ceres.config.item.enableImageCarousel"
                :disable-carousel-on-mobile="disableCarouselOnMobile"
                :is-salable-item="!!item.item && (item.item.salableVariationCount > 0)"
                :enable-av-overlay="enableOverlay"
                :item-identification="selectedVariation.variation.id"
            />
          </slot>
        </div>
      </div>

      <!-- ITEM DETAILS -->
      <div v-if="entries.length > 0" :class="thumbContentClass">
        <template v-for="entry in entries">
          <!-- TITLE -->
          <template v-if="entry.thumbFieldSelection === 'title'">
            <a :href="item | itemURL(urlWithVariationId)" class="title-thumb"
               :class="entry.titleSettings.titleTextColor + ' ' + entry.titleSettings.titleAlign + ' ' + entry.titleSettings.customClass">
              <template v-if="entry.titleSettings.titleSelect === 'name1'">
                {{ item.texts.name1 }}
              </template>
              <template v-else-if="entry.titleSettings.titleSelect === 'name2'">
                {{ item.texts.name2 }}
              </template>
              <template v-else>
                {{ item.texts.name3 }}
              </template>
              <template v-if="entry.titleSettings.showItemAttributes">
                <template v-for="attribute in selectedVariation.groupedAttributes">
                  {{ "Ceres::Template.itemGroupedAttribute" | translate(attribute) }}
                </template>
              </template>
            </a>
          </template>
          <!-- DESCRIPTION -->
          <template v-if="entry.thumbFieldSelection === 'description' && item.texts.shortDescription !== '' ">
            <div class="description-thumb"
                 :class="entry.descriptionSettings.descriptionTextColor + ' ' + entry.descriptionSettings.descriptionAlign + ' ' + entry.descriptionSettings.customClass">
              {{ item.texts.shortDescription }}
            </div>
          </template>
          <!-- PRICE -->
          <template v-if="entry.thumbFieldSelection === 'prices'">
            <div class="prices-thumb" :class="entry.priceSettings.finalPriceAlign + ' ' + entry.priceSettings.customClass">
              <template v-if="entry.priceSettings.showRrp && entry.priceSettings.rrpPosition === 'left' &&
              selectedVariation.prices.rrp && selectedVariation.prices.rrp.price.value > 0 &&
              selectedVariation.prices.rrp.price.value > selectedVariation.prices.default.price.value">
                <div class="before-price">
                  <del v-if="selectedVariation.prices.specialOffer" class="crossprice">
                    {{ selectedVariation.prices.default.unitPrice.formatted | itemCrossPrice(true) }}
                  </del>
                  <del v-else class="crossprice">
                    {{ selectedVariation.prices.rrp.unitPrice.formatted | itemCrossPrice }}
                  </del>
                </div>
                <div class="final-price" :class="entry.priceSettings.rrpColor">
                  <template v-if="selectedVariation.item.itemType === 'set'">
                    {{ $translate("Ceres::Template.itemSetPrice", { price: itemSetPrice }) }} {{ $translate("Ceres::Template.itemFootnote") }}
                  </template>
                  <template v-else-if="itemGraduatedPriceisCheapestSorting">
                    {{ $translate("Ceres::Template.itemFromPrice", { price: itemPriceGraduated }) }} {{ $translate("Ceres::Template.itemFootnote") }}
                  </template>
                  <template v-else-if="itemGraduatedPricesalableVariationCount">
                    {{ $translate("Ceres::Template.itemFromPrice", { price: itemPriceGraduated }) }} {{ $translate("Ceres::Template.itemFootnote") }}
                  </template>
                  <template v-else>
                    {{ selectedVariation.prices.default.unitPrice.formatted | specialOffer(selectedVariation.prices, "unitPrice", "formatted") }} {{ $translate("Ceres::Template.itemFootnote") }}
                  </template>
                </div>
              </template>

              <template v-else-if="entry.priceSettings.showRrp && entry.priceSettings.rrpPosition === 'right' &&
              selectedVariation.prices.rrp && selectedVariation.prices.rrp.price.value > 0 &&
              selectedVariation.prices.rrp.price.value > selectedVariation.prices.default.price.value">
                <div class="final-price" :class="entry.priceSettings.rrpColor">
                  <template v-if="selectedVariation.item.itemType === 'set'">
                    {{ $translate("Ceres::Template.itemSetPrice", { price: itemSetPrice }) }} {{ $translate("Ceres::Template.itemFootnote") }}
                  </template>
                  <template v-else-if="itemGraduatedPriceisCheapestSorting">
                    {{ $translate("Ceres::Template.itemFromPrice", { price: itemPriceGraduated }) }} {{ $translate("Ceres::Template.itemFootnote") }}
                  </template>
                  <template v-else-if="itemGraduatedPricesalableVariationCount">
                    {{ $translate("Ceres::Template.itemFromPrice", { price: itemPriceGraduated }) }} {{ $translate("Ceres::Template.itemFootnote") }}
                  </template>
                  <template v-else>
                    {{ selectedVariation.prices.default.unitPrice.formatted | specialOffer(selectedVariation.prices, "unitPrice", "formatted") }} {{ $translate("Ceres::Template.itemFootnote") }}
                  </template>
                </div>
                <div class="before-price">
                  <del v-if="selectedVariation.prices.specialOffer" class="crossprice">
                    {{ selectedVariation.prices.default.unitPrice.formatted | itemCrossPrice(true) }}
                  </del>
                  <del v-else class="crossprice">
                    {{ selectedVariation.prices.rrp.unitPrice.formatted | itemCrossPrice }}
                  </del>
                </div>
              </template>

              <div v-else class="final-price" :class="entry.priceSettings.finalPriceColor">
                <template v-if="selectedVariation.item.itemType === 'set'">
                  {{ $translate("Ceres::Template.itemSetPrice", { price: itemSetPrice }) }} {{ $translate("Ceres::Template.itemFootnote") }}
                </template>
                <template v-else-if="itemGraduatedPriceisCheapestSorting">
                  {{ $translate("Ceres::Template.itemFromPrice", { price: itemPriceGraduated }) }} {{ $translate("Ceres::Template.itemFootnote") }}
                </template>
                <template v-else-if="itemGraduatedPricesalableVariationCount">
                  {{ $translate("Ceres::Template.itemFromPrice", { price: itemPriceGraduated }) }} {{ $translate("Ceres::Template.itemFootnote") }}
                </template>
                <template v-else>
                  {{ selectedVariation.prices.default.unitPrice.formatted | specialOffer(selectedVariation.prices, "unitPrice", "formatted") }} {{ $translate("Ceres::Template.itemFootnote") }}
                </template>
              </div>

              <slot name="after-prices"></slot>

              <div class="category-lowest-price small" v-if="selectedVariation.prices.default.lowestPrice.value && hasCrossPrice">
                <span v-html="$translate('Ceres::Template.itemLowestPrice', {'price': selectedVariation.prices.default.lowestPrice.formatted})"></span>
              </div>

              <template v-if="entry.priceSettings.showUnitPrice">
                <div v-if="!(selectedVariation.unit.unitOfMeasurement === 'C62' && selectedVariation.unit.content === 1)" class="unit-price small">
                  <span>{{ selectedVariation.unit.content }}</span>
                  <span>&nbsp;{{ selectedVariation.unit.names.name }}</span>
                  <span v-if="selectedVariation.variation.mayShowUnitPrice">&nbsp;| {{ basePrice }}</span>
                </div>
              </template>
            </div>
          </template>
          <!-- CART -->
          <template v-if="entry.thumbFieldSelection === 'cart'">
            <div class="basket-thumb" :class="entry.cartSettings.customClass">
              <custom-add-to-basket
                  :variation-id="selectedVariation.variation.id"
                  :is-salable="!!selectedVariation.filter && selectedVariation.filter.isSalable"
                  :has-children="!!selectedVariation.item && selectedVariation.item.salableVariationCount > 1"
                  :interval-quantity="selectedVariation.variation.intervalOrderQuantity || 1"
                  :minimum-quantity="selectedVariation.variation.minimumOrderQuantity"
                  :maximum-quantity="!!selectedVariation.variation.maximumOrderQuantity && selectedVariation.variation.maximumOrderQuantity > 0 ? selectedVariation.variation.maximumOrderQuantity : null"
                  :order-properties="selectedVariation.properties.filter(function(prop) { return prop.property.isOderProperty })"
                  :has-order-properties="selectedVariation.hasOrderProperties"
                  :has-required-order-property="selectedVariation.hasRequiredOrderProperty"
                  :use-large-scale="false"
                  :show-quantity="false"
                  :item-url="item | itemURL(urlWithVariationId)"
                  :has-price="item | hasItemDefaultPrice"
                  :has-graduated-price="itemGraduatedPriceisCheapestSorting || itemGraduatedPricesalableVariationCount"
                  :item-type="selectedVariation.item.itemType"
                  :basket-not-salable-icon="basketNotSalableIcon"
                  :basket-salable-icon="basketSalableIcon"
              />
            </div>
          </template>
          <!-- VAT -->
          <template v-if="entry.thumbFieldSelection === 'vat'">
            <div class="vat-thumb text-muted" :class="entry.vatSettings.vatAlign + ' ' + entry.vatSettings.customClass">
              {{ $translate("Ceres::Template.itemFootnote") }}
              <template v-if="!showNetPrices">
                <span v-if="entry.vatSettings.vatShowPercentage && selectedVariation.prices.default.vat.value > 0">
                  {{ $translate("Ceres::Template.singleItemIncludeAbbr") }}
                  {{ selectedVariation.prices.default.vat.value }}%
                  {{ $translate("Ceres::Template.basketVAT") }}
                </span>
                <span v-else>{{ $translate("Ceres::Template.itemInclVAT") }}</span>
              </template>
              <span v-else>{{ $translate("Ceres::Template.itemExclVAT") }}</span>
              {{ $translate("Ceres::Template.itemExclusive") }}
              <a v-if="$ceres.config.global.shippingCostsCategoryId > 0" data-toggle="modal" href="#shippingscosts" :class="entry.vatSettings.vatToggleColor" :title="$translate('Ceres::Template.itemShippingCosts')">{{ $translate("Ceres::Template.itemShippingCosts") }}</a>
              <a v-else :title="$translate('Ceres::Template.itemShippingCosts')">{{ $translate("Ceres::Template.itemShippingCosts") }}</a>
            </div>
          </template>
          <!-- AVAILABILITY -->
          <template v-if="entry.thumbFieldSelection === 'availability' && selectedVariation.variation.availability.names.name">
            <div class="availability-thumb" :class="entry.availabilitySettings.availabilityAlign + ' ' + entry.availabilitySettings.customClass">
              {{ $translate("Ceres::Template.wishListAvailability") }} - {{ selectedVariation.variation.availability.names.name }}
            </div>
          </template>
          <!-- PROPERTIES -->
          <template v-if="entry.thumbFieldSelection === 'properties' && item.variationProperties">
            <div class="properties-thumb" :class="entry.propertySettings.propertyAlign + ' ' + entry.propertySettings.customClass">
              <template v-if="isPreview">
                <strong v-if="entry.propertySettings.showProperty">Property Name: </strong>Property Value
              </template>
              <template v-else>
                <template v-for="properyGroup in item.variationProperties">
                  <template v-for="property in properyGroup.properties">
                    <template v-if="property.values.value !== ''">
                      <div>
                        <strong v-if="entry.propertySettings.showProperty">{{ property.names.name }}: </strong>
                        {{ property.values.value }}
                      </div >
                    </template>
                  </template>
                </template>
              </template>
            </div>
          </template>
          <!-- VARIATIONS -->
          <template v-if="entry.thumbFieldSelection === 'variations' && variationsData != null && typeOfVariation === 'image'">
            <div class="variations-thumb" :class="entry.variationsSettings.customClass">
              <ul class="nav justify-content-center v-s-boxes images">
                <li v-for="variation in variationsData.documents" class="nav-item m-2 v-s-box bg-white"
                @click="selectVariation(variation.data, variation.data.attributes[0].value.id)"
                :class="variationClass(variation.data.attributes[0].value.id, variation.data.filter.isSalable)">
                  <img v-if="variation.data.attributes[0].value.image !== '' " class="p-1" :alt="variation.data.attributes[0].value.backendName" :src="`/images/produkte/grp/${variation.data.attributes[0].value.image}`">
                  <span class="mx-3" v-else>{{ variation.data.attributes[0].value.names.name }}</span>
                </li >
              </ul>
            </div>
          </template>
          <!-- FEEDBACK -->
          <div v-if="entry.thumbFieldSelection === 'feedback'" class="feedback-thumb" :class="entry.feedbackSettings.feedbackAlign + ' ' + entry.feedbackSettings.customClass">
              <slot name="before-prices" />
          </div>
        </template>
      </div>
    </div>
  </article>
</template>

<script>
import { mapState } from 'vuex'
import CustomCategoryImage from './CustomCategoryImage.vue'
import CustomItemStoreSpecial from './CustomItemStoreSpecial.vue'
// import CustomAddToBasket from '../Item/CustomAddToBasket.vue';
import {getSlotData} from "../../helper/getSlotData.js";

export default {
  components: {
    CustomCategoryImage,
    CustomItemStoreSpecial,
    // CustomAddToBasket
  },
  props: {
    itemData:
        {
          type: Object
        },
    variationsData:
        {
          type: Object
        },
    entries: {
      type: Array
    },
    singleItemDefaultView: {
      type: Boolean,
      default: true
    },
    singleItemCenterContent: {
      type: Boolean,
      default: false
    },
    isPreview: {
      type: Boolean
    },
    showBasket: {
      type: Boolean,
      default: true
    },
    basketSalableIcon: {
      type: String,
      default: 'cart-plus'
    },
    basketNotSalableIcon: {
      type: String,
      default: 'arrow-right'
    },
    itemClass: {
      type: String,
      default: ''
    },
    datumClass: {
      type: String,
      default: ''
    },
    itemsMobile: {
      type: Number,
      default: 1
    },
    /** Original props */
    enableBadge: {
      type: Boolean,
      default: true
    },
    enableTagBadge: {
      type: Boolean,
      default: true
    },
    enableOverlay: {
      type: Boolean,
      default: true
    },
    badgeText: {
      type: String
    },
    badgeClass: {
      type: String
    },
    badgePosition: {
      type: String
    },
    aspectRatio: {
      type: String
    },
    decimalCount: {
      type: Number,
      default: 0
    },
    imageUrlAccessor:
        {
          type: String,
          default: 'urlMiddle'
        },
    disableCarouselOnMobile:
        {
          type: Boolean
        },
    paddingClasses:
        {
          type: String,
          default: ''
        },
    paddingInlineStyles:
        {
          type: String,
          default: ''
        }
  },
  jsonDataFields: [
    "itemDataRef"
  ],
  data(){
    return{
      attributeSelected : null,
      itemVariationData : null
    }
  },
  computed:
      {
        item () {
          return this.itemData || this.itemSlotData || this.itemDataRef;
        },
        itemSlotData: getSlotData('item-data'),

        selectedVariation(){
          if( (this.attributeSelected === null) ){
            return this.itemData || this.itemSlotData || this.itemDataRef;
          }else{
            return this.itemVariationData
          }
        },
        thumbContainerClass(){
          if(this.itemsMobile == 1 && this.singleItemDefaultView && this.singleItemCenterContent)
            return 'thumb-container'
          else
            return ''
        },
        thumbContentClass(){
          if(this.itemsMobile == 1 && !this.singleItemDefaultView)
            return 'thumb-content-v2'
          else
            return 'thumb-content'
        },
        thumbImageClass(){
          if(this.itemsMobile == 1 && !this.singleItemDefaultView)
            return 'thumb-image-v2'
          else
            return 'thumb-image'
        },
        itemSlotData: getSlotData('item-data'),

        typeOfVariation(){
          if(this.variationsData != null && this.variationsData.total > 0){
            if(this.variationsData.documents[0].data.attributes.length != 0){
              if(this.variationsData.documents[0].data.attributes[0].attribute.typeOfSelectionInOnlineStore === 'image' ){
                return 'image'
              }else if(this.variationsData.documents[0].data.attributes[0].attribute.typeOfSelectionInOnlineStore === 'box' ){
                return 'box'
              }else{
                return 'dropdown'
              }
            }else{
              return 'undefined'
            }
          }else{
            return 'undefined'
          }
        },
        /**
         * returns itemData.item.storeSpecial
         */
        storeSpecial () {
          return this.item.item.storeSpecial
        },
        /**
         * returns itemData.texts
         */
        texts()
        {
            return this.item.texts;
        },
         itemPrice()
        {
            return this.$options.filters.specialOffer(this.item.prices.default.unitPrice.formatted, this.item.prices, "unitPrice", "formatted" );
        },
        basePrice()
        {
          return this.item.prices.default.basePrice;
        },
        itemPriceGraduated()
        {
          let unitPrice;
          if (App.config.item.enableGraduatedPrices) {
            unitPrice = this.item.prices.graduatedPrices.length > 0
                ? this.item.prices.graduatedPrices[0].unitPrice
                : this.item.prices.default.unitPrice;
          } else {
            unitPrice = this.item.prices.default.unitPrice;
          }

          return this.$options.filters.specialOffer(unitPrice.formatted, this.item.prices, "unitPrice", "formatted" );
        },
        itemGraduatedPriceisCheapestSorting()
        {
          return !!this.item.item && this.item.item.salableVariationCount > 1 && !!this.$ceres.isCheapestSorting;
        },
        itemGraduatedPricesalableVariationCount()
        {
          return !!this.item.item && this.item.item.salableVariationCount == 1 && this.item.prices.graduatedPrices.length > 1 && App.config.item.enableGraduatedPrices;
        },
        itemSetPrice()
        {
          return this.$options.filters.currency(
              this.item.prices.default.price.value,
              this.item.prices.default.currency
          );
        },

        urlWithVariationId(){
            return !this.$ceres.config.item.showPleaseSelect || this.$ceres.initialPleaseSelect == 0 || this.forceUrlWithVariationId;
        },
        hasCrossPrice() {
            const hasRrpPrice = !!this.item.prices.rrp &&
                this.item.prices.rrp.unitPrice.value > this.item.prices.default.unitPrice.value;

            const hasBeforePrice = !!this.item.prices.specialOffer &&
                !!this.item.prices.default &&
                this.item.prices.default.unitPrice.value > this.item.prices.specialOffer.unitPrice.value;

            return hasRrpPrice || hasBeforePrice;
        },
        ...mapState({
          showNetPrices: state => state.basket.showNetPrices
        })
      },
  methods: {
    selectVariation(variationData, attributeId){
      this.itemVariationData = variationData
      this.attributeSelected = attributeId
      this.$nextTick(() =>
      {
        this.$refs.CustomCategoryImage.destroyCarousel()
        this.$refs.CustomCategoryImage.initializeCarousel()
      });
    },
    variationClass(attributeId, isSalable){
      if( (this.attributeSelected === attributeId) ){
        return 'active'
      }else if( !(this.attributeSelected === attributeId) && !isSalable ){
        return 'invalid'
      }else{
        return ''
      }
    }
  }
}
</script>
